import { WidgetElementType } from "@redotech/redo-storefront-common/widget-config";
import { CoverageCreditIncentiveRewardType } from "./coverage-types";

/** The info card text keys; the storefront shows the one for the products the merchant offers. */
export const INFO_CARD_TEXT_KEYS = [
  "returns",
  "packageProtection",
  "both",
] as const;

export type InfoCardTextKey = (typeof INFO_CARD_TEXT_KEYS)[number];

/** A missing reward type means store credit, as everywhere else. */
type InfoCardRewardType = CoverageCreditIncentiveRewardType | null | undefined;

const PRODUCT_SEGMENTS: Record<InfoCardTextKey, readonly string[]> = {
  returns: ["free returns"],
  packageProtection: ["package protection"],
  both: ["free returns", "package protection"],
};

const SEPARATOR = " + ";

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function rewardSegment(rewardType: InfoCardRewardType): string {
  return rewardType === CoverageCreditIncentiveRewardType.DISCOUNT_CODE
    ? "%coverageCreditAmount% off your next order"
    : "%coverageCreditAmount% store credit";
}

/**
 * The info card subtitle the "Auto adjust Checkout+ info card" setting writes.
 *
 * `%if coverageCredit%` is false below the incentive's minimum order, because
 * the credit amount is 0 there. The storefront trims each branch, so a " + "
 * left at the end of a branch would lose its trailing space; each branch is
 * therefore a complete line.
 */
export function creditIncentiveInfoCardSubtitle(
  key: InfoCardTextKey,
  rewardType: InfoCardRewardType,
): string {
  const products = PRODUCT_SEGMENTS[key];
  const withCredit = [rewardSegment(rewardType), ...products].join(SEPARATOR);
  const withoutCredit = capitalizeFirst(products.join(SEPARATOR));
  return `%if coverageCredit%${withCredit}%else%${withoutCredit}%endif%`;
}

interface TitleSubtitle {
  titleText?: string | null;
  subtitleText?: string | null;
}

export type InfoCardText = Partial<
  Record<InfoCardTextKey, TitleSubtitle | null | undefined>
>;

interface WidgetConfigLike {
  elementType?: string | null;
  cardText?: InfoCardText | null;
}

export function isInfoCardWidgetConfig(config: WidgetConfigLike): boolean {
  return config.elementType === WidgetElementType.INFO_CARD;
}

/** Card text with every subtitle replaced by the generated copy; titles are kept. */
export function cardTextWithCreditIncentiveSubtitles(
  cardText: InfoCardText | null | undefined,
  rewardType: InfoCardRewardType,
): Record<InfoCardTextKey, { titleText: string; subtitleText: string }> {
  const entry = (key: InfoCardTextKey) => ({
    titleText: cardText?.[key]?.titleText ?? "",
    subtitleText: creditIncentiveInfoCardSubtitle(key, rewardType),
  });
  return {
    returns: entry("returns"),
    packageProtection: entry("packageProtection"),
    both: entry("both"),
  };
}

/** Whether every subtitle on the card is already the generated copy. */
export function cardTextHasCreditIncentiveSubtitles(
  cardText: InfoCardText | null | undefined,
  rewardType: InfoCardRewardType,
): boolean {
  return INFO_CARD_TEXT_KEYS.every(
    (key) =>
      cardText?.[key]?.subtitleText ===
      creditIncentiveInfoCardSubtitle(key, rewardType),
  );
}

/** Whether the subtitle is copy the merchant wrote: not blank, and not generated copy for either reward type. */
export function isCustomInfoCardSubtitle(
  key: InfoCardTextKey,
  subtitle: string | null | undefined,
): boolean {
  return (
    !!subtitle &&
    !Object.values(CoverageCreditIncentiveRewardType).some(
      (rewardType) =>
        subtitle === creditIncentiveInfoCardSubtitle(key, rewardType),
    )
  );
}

/** Whether turning the setting on would replace copy the merchant wrote on some info card. */
export function infoCardsHaveCustomSubtitles(
  widgetConfigs: readonly WidgetConfigLike[] | null | undefined,
): boolean {
  return (widgetConfigs ?? []).some(
    (config) =>
      isInfoCardWidgetConfig(config) &&
      INFO_CARD_TEXT_KEYS.some((key) =>
        isCustomInfoCardSubtitle(key, config.cardText?.[key]?.subtitleText),
      ),
  );
}
